import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { useApi } from '@backstage/core-plugin-api';
import { spassuCodeApiRef } from '../../api';
import {
  AzureWorkItemSummary,
  AzureWorkItemType,
  PaginatedWorkItems,
  SearchWorkItemsFilters,
} from '../../api/types';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  filterControl: {
    minWidth: 140,
  },
  searchInput: {
    flex: 1,
    minWidth: 200,
  },
  tableContainer: {
    maxHeight: 320,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  row: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedRow: {
    backgroundColor: theme.palette.action.selected,
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
  },
  wiId: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  wiTitle: {
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  stateChip: {
    fontSize: '0.7rem',
    height: 20,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(0.5),
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  loadingOverlay: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

export interface WorkItemPickerProps {
  projectId: string;
  onSelect: (workItem: AzureWorkItemSummary) => void;
  selectedId?: number;
  defaultType?: string;
  initialFilters?: Partial<SearchWorkItemsFilters>;
  lockAssignedToMe?: boolean;
  renderEmpty?: (filters: SearchWorkItemsFilters) => ReactNode;
}

const PAGE_SIZE = 50;

export const WorkItemPicker = ({
  projectId,
  onSelect,
  selectedId,
  defaultType,
  initialFilters,
  lockAssignedToMe = false,
  renderEmpty,
}: WorkItemPickerProps) => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);

  // Work item types (fetched once)
  const [wiTypes, setWiTypes] = useState<AzureWorkItemType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  // Filters
  const [selectedType, setSelectedType] = useState<string>(defaultType ?? '');
  const [selectedStates, setSelectedStates] = useState<string[]>(
    initialFilters?.states ?? [],
  );
  const [assignedToMe, setAssignedToMe] = useState(
    initialFilters?.assignedToMe ?? false,
  );
  const [searchText, setSearchText] = useState('');
  const [debouncedText, setDebouncedText] = useState('');
  const [page, setPage] = useState(0);

  // Results
  const [results, setResults] = useState<PaginatedWorkItems | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce search text (400ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedText(searchText), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Fetch types on mount
  useEffect(() => {
    let cancelled = false;
    setLoadingTypes(true);
    api.listWorkItemTypes(projectId).then(types => {
      if (!cancelled) {
        setWiTypes(types);
        setLoadingTypes(false);
      }
    }).catch(() => {
      if (!cancelled) setLoadingTypes(false);
    });
    return () => { cancelled = true; };
  }, [api, projectId]);

  // Available states from type metadata
  const availableStates = useMemo(() => {
    const stateSet = new Set<string>();
    for (const t of wiTypes) {
      for (const s of t.states ?? []) {
        stateSet.add(s.name);
      }
    }
    return Array.from(stateSet).sort();
  }, [wiTypes]);

  // Build filters object
  const currentFilters = useMemo<SearchWorkItemsFilters>(() => {
    const isIdSearch = debouncedText.startsWith('#') && /^\d+$/.test(debouncedText.slice(1));
    return {
      projectId,
      types: selectedType ? [selectedType] : undefined,
      states: selectedStates.length > 0 ? selectedStates : undefined,
      assignedToMe,
      text: isIdSearch ? undefined : (debouncedText || undefined),
      workItemId: isIdSearch ? Number(debouncedText.slice(1)) : undefined,
      top: PAGE_SIZE,
      skip: page * PAGE_SIZE,
    };
  }, [projectId, selectedType, selectedStates, assignedToMe, debouncedText, page]);

  // Fetch results when filters change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.searchWorkItems(projectId, currentFilters).then(data => {
      if (!cancelled) {
        setResults(data);
        setLoading(false);
      }
    }).catch(err => {
      if (!cancelled) {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [api, projectId, currentFilters]);

  // Reset page when filters change (except page itself)
  const resetPage = useCallback(() => setPage(0), []);

  const handleTypeChange = useCallback((value: string) => {
    setSelectedType(value);
    resetPage();
  }, [resetPage]);

  const handleStateChange = useCallback((value: string[]) => {
    setSelectedStates(value);
    resetPage();
  }, [resetPage]);

  const handleAssignedToMe = useCallback(() => {
    if (lockAssignedToMe) return;
    setAssignedToMe(prev => !prev);
    resetPage();
  }, [lockAssignedToMe, resetPage]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    resetPage();
  }, [resetPage]);

  const total = results?.total ?? 0;
  const from = page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);
  const hasNext = results?.hasMore ?? false;
  const hasPrev = page > 0;

  return (
    <div className={classes.root}>
      {/* Filters bar */}
      <div className={classes.filters}>
        <FormControl className={classes.filterControl} size="small" variant="outlined">
          <InputLabel shrink>Tipo</InputLabel>
          <Select
            value={selectedType}
            onChange={e => handleTypeChange(e.target.value as string)}
            label="Tipo"
            displayEmpty
            disabled={loadingTypes}
          >
            <MenuItem value="">Todos</MenuItem>
            {wiTypes.map(t => (
              <MenuItem key={t.name} value={t.name}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.filterControl} size="small" variant="outlined">
          <InputLabel>Estado</InputLabel>
          <Select
            multiple
            value={selectedStates}
            onChange={e => handleStateChange(e.target.value as string[])}
            label="Estado"
            renderValue={sel => (sel as string[]).join(', ')}
          >
            {availableStates.map(s => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={assignedToMe}
              onChange={handleAssignedToMe}
              color="primary"
              size="small"
              disabled={lockAssignedToMe}
            />
          }
          label={<Typography variant="body2">Atribuídas a mim</Typography>}
        />

        <TextField
          className={classes.searchInput}
          size="small"
          variant="outlined"
          placeholder="Buscar por título, descrição ou #ID..."
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          InputProps={{
            endAdornment: loading ? <CircularProgress size={16} /> : undefined,
          }}
        />
      </div>

      {/* Results */}
      {error && (
        <Typography color="error" variant="body2">{error}</Typography>
      )}

      {loading && !results && (
        <div className={classes.loadingOverlay}>
          <CircularProgress size={24} />
        </div>
      )}

      {!loading && results && results.data.length === 0 && (
        <div className={classes.emptyState}>
          {renderEmpty ? (
            renderEmpty(currentFilters)
          ) : (
            <Typography variant="body2" color="textSecondary">
              Nenhum Work Item encontrado. Refine os filtros.
            </Typography>
          )}
        </div>
      )}

      {results && results.data.length > 0 && (
        <>
          <div className={classes.tableContainer}>
            <Table size="small" aria-label="Work Items">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Atribuído a</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.data.map(wi => (
                  <TableRow
                    key={wi.id}
                    onClick={() => onSelect(wi)}
                    className={`${classes.row} ${wi.id === selectedId ? classes.selectedRow : ''}`}
                    selected={wi.id === selectedId}
                    role="option"
                    aria-selected={wi.id === selectedId}
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') onSelect(wi);
                    }}
                  >
                    <TableCell className={classes.wiId}>#{wi.id}</TableCell>
                    <TableCell>
                      <Chip
                        label={wi.type}
                        size="small"
                        className={classes.stateChip}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={wi.title} arrow>
                        <span className={classes.wiTitle}>{wi.title}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip label={wi.state} size="small" className={classes.stateChip} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {wi.assignedTo ?? '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className={classes.pagination}>
            <Typography variant="caption" color="textSecondary">
              {from}–{to} de {total}
            </Typography>
            <div>
              <IconButton
                size="small"
                disabled={!hasPrev}
                onClick={() => setPage(p => p - 1)}
                aria-label="Página anterior"
              >
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton
                size="small"
                disabled={!hasNext}
                onClick={() => setPage(p => p + 1)}
                aria-label="Próxima página"
              >
                <NavigateNextIcon />
              </IconButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
